import { Analytics } from '@cobliteam/analytics';
import Avatar, { AvatarFallback, AvatarImage } from '@cobliteam/colib-ui/Avatar';
import Button from '@cobliteam/colib-ui/Button';
import type { SessionUser } from '@cobliteam/common-types';
import useBasicValueMaturity from '@cobliteam/hooks/src/useBasicValueMaturity/useBasicValueMaturity';
import useIsOnboardingFleet from '@cobliteam/hooks/src/useIsOnboardingFleet/useIsOnboardingFleet';
import useUserAvatar from '@cobliteam/hooks/src/useSession/useUserAvatar';
import { useTranslation } from '@cobliteam/i18n';
import { lazy } from '@cobliteam/lazy-with-retries';
import { BASIC_VALUE_STEP_PERMISSIONS, usePermissions } from '@cobliteam/permissions';
import Icon from '@cobliteam/ui/icons';
import { Suspense, useState } from 'react';

// O menu (Base UI) sai do chunk inicial (ADR-0022): o cabeçalho monta em toda página.
const AppUserMenu = lazy(() => import('./AppUserMenu'));

interface AppUserProps {
  user: SessionUser;
  logout: () => void;
}

/**
 * Pending "!" highlight on the avatar while the fleet is in onboarding and setup is incomplete. Also
 * mirrored on the "Guia de configuração" menu item so the user knows where the "!" points. The
 * onboarding window decides whether the "!" shows, not whether the guide is reachable.
 */
const useBasicValuePending = (hasBasicValueAccess: boolean) => {
  const { isOnboardingFleet } = useIsOnboardingFleet();
  // O progresso só alimenta o "!": frotas fora do onboarding ou sem permissão não pagam as ~10 listas.
  const shouldTrackMaturity = isOnboardingFleet && hasBasicValueAccess;
  const { isComplete, isLoading } = useBasicValueMaturity({ enabled: shouldTrackMaturity });

  return shouldTrackMaturity && !(isLoading || isComplete);
};

export default function AppUser({ user, logout }: AppUserProps) {
  const { t } = useTranslation('app-header');
  const { isAccessAllowed } = usePermissions();
  // O item de menu do "Guia de configuração" depende só de permissão (OR das permissões das etapas —
  // AE-2446): a página é acessível a qualquer frota, com ou sem 90 dias de contrato.
  const hasBasicValueAccess = isAccessAllowed(BASIC_VALUE_STEP_PERMISSIONS);
  const basicValuePending = useBasicValuePending(hasBasicValueAccess);
  // Foto de perfil enviada em /config/account. Mesma query key do `UpdateUserAvatar`, então
  // um upload/remoção lá reflete aqui na hora, sem reload.
  const { avatarUrl } = useUserAvatar();
  const [open, setOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) Analytics.track('UserButton - Open');
    setOpen(isOpen);
  };

  const renderButton = (onClick?: () => void) => (
    <Button
      variant='outline'
      size='sm'
      data-testid='app-user'
      aria-label={basicValuePending ? t('user.menuAriaPending') : t('user.menuAria')}
      className='relative size-8 overflow-hidden rounded-full p-0'
      onClick={onClick}
    >
      {/* Radix Avatar só troca para a imagem depois que ela carrega, então o ícone segue sendo
          o estado padrão (sem foto, carregando, ou URL quebrada). */}
      <Avatar className='size-8 rounded-full'>
        {avatarUrl && <AvatarImage src={avatarUrl} alt='' data-testid='app-user-avatar-image' />}
        <AvatarFallback>
          <Icon name='user' size='md' />
        </AvatarFallback>
      </Avatar>
    </Button>
  );

  const badge = basicValuePending ? (
    <span
      aria-hidden
      data-testid='app-user-pending-badge'
      className='pointer-events-none absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-warning-9 font-bold text-[9px] text-white leading-none ring-2 ring-white'
    >
      !
    </span>
  ) : null;

  // Enquanto o chunk do menu carrega, o clique fica guardado no estado: o menu controlado já abre ao montar.
  return (
    <Suspense
      fallback={
        <span className='relative inline-flex'>
          {renderButton(() => handleOpenChange(true))}
          {badge}
        </span>
      }
    >
      <AppUserMenu
        user={user}
        logout={logout}
        open={open}
        onOpenChange={handleOpenChange}
        trigger={renderButton()}
        badge={badge}
        hasBasicValueAccess={hasBasicValueAccess}
        basicValuePending={basicValuePending}
      />
    </Suspense>
  );
}
